'use client';

import { useEffect, useState } from 'react';

export type LandscapeState = {
  /** Vrai quand l'appareil est tenu à l'horizontale. */
  landscape: boolean;
  /** Côté par lequel le feuillet entre : celui du bord BAS physique du
   *  téléphone. Penché à gauche → il ressort à droite, et inversement. */
  side: 'left' | 'right';
  /** Largeur de l'écran tel qu'il serait tenu en portrait, en px.
   *  La navigation garde cette largeur en paysage au lieu de s'étirer. */
  portraitW: number;
  /** Dimensions courantes du viewport. Elles sortent d'ici plutôt que d'un
   *  état local à chaque page : la rotation iOS publie ses nouvelles valeurs
   *  avec du retard, et ce hook est le seul à porter les rattrapages qu'il
   *  faut pour ça. Deux sources = une qui bascule avant l'autre, donc des
   *  bornes calculées sur l'ancienne largeur. */
  w: number;
  h: number;
};

/**
 * Orientation de l'appareil, pour un affichage en split :
 *
 *   téléphone penché à GAUCHE            téléphone penché à DROITE
 *   (bord bas physique à droite)         (bord bas physique à gauche)
 *   +----------------+------+            +------+----------------+
 *   |     carte      |feuil-|            |feuil-|     carte      |
 *   |  [ navbar ]    | let  |            | let  |    [ navbar ]  |
 *   +----------------+------+            +------+----------------+
 *
 * Le feuillet suit le bord bas du téléphone — il continue de sortir « du bas »
 * du point de vue de l'appareil. La navigation, elle, reste visuellement en
 * bas, poussée du côté opposé pour laisser la place au feuillet.
 *
 * Le seuil porte sur la HAUTEUR, pas sur le ratio : ce qui distingue le
 * paysage, ce n'est pas d'être large mais d'être bas. Une tablette posée ou un
 * écran de bureau gardent donc la disposition normale.
 *
 * Démarre en portrait des deux côtés (SSR + premier rendu client) : aucun
 * désaccord d'hydratation, la vraie valeur arrive au montage.
 */
export function useLandscape(): LandscapeState {
  const [state, setState] = useState<LandscapeState>({ landscape: false, side: 'right', portraitW: 0, w: 0, h: 0 });

  useEffect(() => {
    const sync = () => {
      const w = window.innerWidth, h = window.innerHeight;
      // `angle` = rotation appliquée au DOCUMENT pour compenser celle de
      // l'appareil. 90° → l'appareil a tourné dans l'autre sens (penché à
      // gauche), son bord bas se retrouve donc à droite de l'écran.
      const angle = window.screen?.orientation?.angle ?? 0;
      const next: LandscapeState = {
        landscape: w > h && h <= 560,
        side: angle === 270 ? 'left' : 'right',
        // Le petit côté de l'écran EST la largeur en portrait : rien à
        // mémoriser d'une orientation à l'autre, la mesure reste juste même
        // si l'app démarre déjà couchée.
        portraitW: Math.min(w, h),
        w, h,
      };
      // Même valeur = même objet. Sans cette garde, les rattrapages et le
      // `resize` que déclenche la barre d'URL au défilement re-rendaient toute
      // la page à chaque fois, pour des dimensions identiques.
      setState(p => (p.landscape === next.landscape && p.side === next.side
        && p.portraitW === next.portraitW && p.w === next.w && p.h === next.h ? p : next));
    };
    // orientationchange : iOS publie parfois les nouvelles dimensions APRÈS
    // l'événement (même course que le bootstrap --actual-vh du layout). Les
    // rattrapages sont donc REPOSÉS à chaque annonce, et pas seulement au
    // montage : une rotation en cours de session tombait sinon sur les
    // dimensions de l'orientation qu'on venait de quitter, sans rien pour la
    // corriger ensuite.
    let t: ReturnType<typeof setTimeout>[] = [];
    const resync = () => {
      sync();
      t.forEach(clearTimeout);
      t = [50, 200, 500].map(d => setTimeout(sync, d));
    };
    resync();
    window.addEventListener('resize', resync);
    window.addEventListener('orientationchange', resync);
    // Retour dans l'app (après Plans, Waze, un appel) et barre d'outils iOS qui
    // se replie : `resize` ne part pas toujours, la hauteur restait l'ancienne.
    const resyncVisible = () => { if (document.visibilityState === 'visible') resync(); };
    document.addEventListener('visibilitychange', resyncVisible);
    window.addEventListener('pageshow', resync);
    window.visualViewport?.addEventListener('resize', resync);
    return () => {
      window.removeEventListener('resize', resync);
      window.removeEventListener('orientationchange', resync);
      document.removeEventListener('visibilitychange', resyncVisible);
      window.removeEventListener('pageshow', resync);
      window.visualViewport?.removeEventListener('resize', resync);
      t.forEach(clearTimeout);
    };
  }, []);

  return state;
}
